import { useEffect, useState } from "react";
import { useGraphToken } from "../auth/useGraphToken";
import { GRAPH_BASE } from "../api/graph";

// Photos are kept for the session: people don't change them often, and the
// same faces show up in several places.
const cache = new Map<string, Promise<string | null>>();

/** Microsoft 365 photos for these people, by lowercase email. Anyone without
 *  one is simply missing from the result. */
export function usePeoplePhotos(emails: string[]): Record<string, string> {
  const getToken = useGraphToken();
  const [photos, setPhotos] = useState<Record<string, string>>({});
  const key = [...new Set(emails.map((e) => e.toLowerCase()).filter(Boolean))].sort().join(",");

  useEffect(() => {
    let alive = true;
    const list = key ? key.split(",") : [];
    void (async () => {
      let token: string;
      try {
        token = await getToken();
      } catch {
        return;
      }
      await Promise.all(
        list.map(async (email) => {
          if (!cache.has(email)) {
            cache.set(
              email,
              fetch(`${GRAPH_BASE}/users/${encodeURIComponent(email)}/photos/96x96/$value`, {
                headers: { Authorization: `Bearer ${token}` },
              })
                .then(async (r) => (r.ok ? URL.createObjectURL(await r.blob()) : null))
                .catch(() => null)
            );
          }
          const url = await cache.get(email)!;
          if (url && alive) setPhotos((prev) => (prev[email] ? prev : { ...prev, [email]: url }));
        })
      );
    })();
    return () => {
      alive = false;
    };
  }, [key, getToken]);

  return photos;
}
